/**
 * Geometry and event helpers shared by the lesson files. Checks read flight state and sim events,
 * never radio text, so they stay deterministic whichever brain is speaking.
 */
import { alongTrackM, crossTrackM, destination, findRunwayEnd, NM_TO_M, toMagneticDeg, toTrueDeg, wrap180, type AirportData, type RunwayEnd } from '@atc/core';
import { spokenCallsign, type Aircraft, type Sim, type SimEvent } from '@atc/sim';
import type { StepContext, StepResult } from './lesson';

export const ft = (n: number) => `${Math.round(n).toLocaleString('en-US')} ft`;
export const offBy = (a: number, b: number) => Math.abs(wrap180(a - b));
/** Controller vectors use practical five-degree increments; runway courses remain exact geometry. */
export const vectorHeading = (degrees: number) => ((Math.round((((degrees % 360) + 360) % 360) / 5) * 5) % 360) || 360;
export const find = (ctx: StepContext, cs: string): Aircraft | undefined => ctx.sim.find(cs);
/** A vector the controller would say for a true direction: magnetic, in five-degree steps. */
export const magneticVector = (airport: AirportData, trueDeg: number) => vectorHeading(toMagneticDeg(airport, trueDeg));
/** Degrees between the heading an aircraft flies (true) and a heading it was given (magnetic, as said). */
export const flying = (airport: AirportData, a: Aircraft, magneticDeg: number) => offBy(a.headingDeg, toTrueDeg(airport, magneticDeg));
/**
 * The verdict when the aircraft a step needs has left the frequency. How it left decides it: a
 * handoff or a landing is the outcome the exercise was driving at, so a step still waiting on it
 * is passed with a note, not failed — a player who worked ahead did the job, just not in the
 * lesson's order. Every other ending is a real loss and names its own reason.
 */
export function gone(ctx: StepContext, cs: string): StepResult {
  const end = [...ctx.all].reverse().find((e): e is Extract<SimEvent, { type: 'removed' }> => e.type === 'removed' && e.ac.callsign === cs);
  switch (end?.ac.phase) {
    case 'handedOff': return { pass: `${cs} was handed off before this step came round — you had already taken it past this point.` };
    case 'landed': return { pass: `${cs} was on the ground before this step came round — you had already taken it past this point.` };
    case 'exited': return { fail: `${cs} left the airspace without a handoff. Keep it inside the radar picture and hand it off at its exit fix.` };
    case 'cancelled': return { fail: `${cs} gave up waiting and went back to the stand. Answer a waiting aircraft before it runs out of patience.` };
    case 'forcedLanding': return { fail: `${cs} ran out of fuel and had to put down. Get a low-fuel flight onto a runway first.` };
    default: return { fail: `${cs} is no longer on your frequency. Start the lesson again.` };
  }
}
export const sawFor = (ctx: StepContext, type: SimEvent['type'], cs: string) =>
  ctx.events.some((e) => e.type === type && 'ac' in e && e.ac.callsign === cs);
/** sawFor over the whole lesson, for an outcome the player may have reached before this step. */
export const everSawFor = (ctx: StepContext, type: SimEvent['type'], cs: string) =>
  ctx.all.some((e) => e.type === type && 'ac' in e && e.ac.callsign === cs);
/** A conflict event naming both of these two, in either order. */
export const pairIn = (e: SimEvent, a: string, b: string) =>
  'a' in e && 'b' in e && [e.a.callsign, e.b.callsign].includes(a) && [e.a.callsign, e.b.callsign].includes(b);
export const asked = (ctx: StepContext, cs: string, subject: string, from = 0) =>
  ctx.events.slice(from).some((e) => e.type === 'pilotQuery' && e.ac.callsign === cs && e.subject === subject);

export function runway(airport: AirportData, name: string): RunwayEnd {
  const rw = findRunwayEnd(airport, name);
  if (!rw) throw new Error(`${airport.icao} has no runway ${name}`);
  return rw;
}

/** A point `backNm` before the threshold, `rightNm` right of the extended centreline (looking along the landing direction). */
export function onFinal(rw: RunwayEnd, backNm: number, rightNm: number) {
  const back = destination(rw.lat, rw.lon, (rw.headingDeg + 180) % 360, backNm * NM_TO_M);
  return destination(back.lat, back.lon, (rw.headingDeg + 90) % 360, rightNm * NM_TO_M);
}

/** Distance back along the extended final, nm; negative once past the threshold. */
export const backNm = (rw: RunwayEnd, p: { lat: number; lon: number }) =>
  -alongTrackM(rw.lat, rw.lon, rw.headingDeg, p.lat, p.lon) / NM_TO_M;
/** Offset right of the extended centreline, nm. */
export const offsetNm = (rw: RunwayEnd, p: { lat: number; lon: number }) =>
  crossTrackM(rw.lat, rw.lon, rw.headingDeg, p.lat, p.lon) / NM_TO_M;
/** Gap along the final between a leader and its follower, nm. */
export const gapNm = (rw: RunwayEnd, lead: { lat: number; lon: number }, follow: { lat: number; lon: number }) =>
  backNm(rw, follow) - backNm(rw, lead);

/** The callsign as the radio says it, so "Say it" matches what the voice path accepts. */
export const spoken = (airport: AirportData, cs: string) => spokenCallsign(cs, cs.replace(/\d.*$/, ''), airport.xAtc?.telephony);

/**
 * An arrival that flew the SALAM 2B from the south to GEMDA, where the chart ends in radar vectors,
 * and has been turned onto a left downwind for runway 12: `backNm` along the final, `leftNm` to its
 * left (the northeast), heading the reciprocal of the final, with its filed STAR kept.
 */
export function onDownwind(sim: Sim, airport: AirportData, rw: RunwayEnd, cs: string, type: string, backNm: number, leftNm: number, altitudeFt: number, iasKt: number): Aircraft {
  const ac = sim.addAircraft({ kind: 'arrival', callsign: cs, type, star: 'SALAM2B', at: 'GEMDA', altitudeFt, iasKt });
  const p = onFinal(rw, backNm, -leftNm);
  const heading = magneticVector(airport, rw.headingDeg + 180);
  ac.lat = p.lat; ac.lon = p.lon;
  ac.headingDeg = ac.trackDeg = toTrueDeg(airport, heading);
  ac.nav = { mode: 'heading', headingDeg: ac.headingDeg, routeIdx: 0 };
  ac.assigned = { ...ac.assigned, headingDeg: heading, altitudeFt };
  ac.targetIasKt = ac.assigned.speedKt = iasKt;
  return ac;
}
